import React from "reactn";

import { calcEaseInOut } from "js/core/utilities/utilities";
import { $, _, WordCloud, SVG } from "js/vendor";
import * as geom from "js/core/utilities/geom";
import { BaseElement } from "js/elements/base/BaseElement";
import { ForeColorType, BackgroundStyleType } from "common/constants";
import { getCSSTransform } from "js/core/utilities/geom";
import { SVGGroup } from "js/core/utilities/svgHelpers";
import Spinner from "js/react/components/Spinner";

class WordCloudElement extends BaseElement {
    get scale() {
        return this.model.scale || 25;
    }

    get style() {
        return this.model.style || "straight";
    }

    get spacing() {
        return (this.model.spacing !== undefined) ? this.model.spacing : 1;
    }

    get wordList() {
        return JSON.parse(this.model.wordcloud);
    }

    set wordList(value) {
        this.model.wordcloud = JSON.stringify(value);
    }

    get usedFontIds() {
        const fontIds = super.usedFontIds;
        // Special case for the "block" font option
        if (!fontIds.includes(this.fontId)) {
            fontIds.push(this.fontId);
        }
        return fontIds;
    }

    refreshElement(transition) {
        this.canvas.refreshElement(this, transition);
    }

    get canRefreshElement() {
        return true;
    }

    get fontType() {
        return this.model.font || "block";
    }

    get fontId() {
        return this.fontType === "theme" ? this.styles.fontId : "bebasneue";
    }

    get shouldLoadOpentypeFonts() {
        return true;
    }

    _build() {
        this.needsLayoutMigration = this.model.version == null || this.model.version < 2;
    }

    _calcProps(props, options) {
        const { size } = props;
        const { isTryingLayout } = options;

        // Asked to regen by the editor
        if (this.shouldRegen) {
            this.shouldRegen = false;
            this.canvas.layouter.runPostRender(() => this.generateWordCloud(size, true));
            return { size, isLoading: true };
        }

        // Migration, we don't have to save the model immediately, it will be saved
        // upon next word cloud recalc, until then we can just assign this value
        if (!this.model.generatedForSize) {
            this.model.generatedForSize = size.toArray();
        }

        // Nothing has changed or trying layout
        if (_.isEqual(this.model.generatedForSize, size.toArray()) || isTryingLayout) {
            return { size, isLoading: false };
        }

        // Something has changed (i.e. layout)
        this.canvas.layouter.runPostRender(() => this.generateWordCloud(size, false));
        return { size, isLoading: true };
    }

    renderChildren(props, transition) {
        const { isLoading, bounds, styles } = props;

        if (isLoading) {
            return <Spinner key={this.id} />;
        }

        this.renderedWords = [];
        const children = [];
        const wordList = this.canvas.isAnimating ? this.animationWordList : this.wordList;
        for (const word of wordList) {
            const font = this.fonts[this.fontId];
            const fontWeight = word.weight === "5" ? 700 : 400;
            const fontStyle = font.getStyle(fontWeight, false);

            const path = fontStyle.font.getPath(word.word, 0, 0, word.layout.fontSize);

            word.textXOffset = (path.getBoundingBox().x2 - path.getBoundingBox().x1) * -0.5;
            word.textYOffset = path.getBoundingBox().y1 * -0.5;

            const bbox = path.getBoundingBox();
            if (word.layout.rotation) {
                const rotation = word.layout.rotation;

                const center = new geom.Point((bbox.x2 - bbox.x1) / 2 + bbox.x1, (bbox.y2 - bbox.y1) / 2 + bbox.y1);

                path.commands.forEach(command => {
                    if (command.x != undefined) {
                        const point = new geom.Point(command.x, command.y).rotate(rotation, center);
                        command.x = point.x;
                        command.y = point.y;
                    }
                    if (command.x1 != undefined) {
                        const point = new geom.Point(command.x1, command.y1).rotate(rotation, center);
                        command.x1 = point.x;
                        command.y1 = point.y;
                    }
                    if (command.x2 != undefined) {
                        const point = new geom.Point(command.x2, command.y2).rotate(rotation, center);
                        command.x2 = point.x;
                        command.y2 = point.y;
                    }
                });
            }

            let matrix = new SVG.Matrix();
            if (!this.needsLayoutMigration) {
                matrix = matrix.translate(-bounds.width / 2, -bounds.height / 2);
            }
            matrix = matrix.translate(word.layout.x, word.layout.y);
            matrix = matrix.translate(word.textXOffset, word.textYOffset);

            let opacity = 1;
            if (this.canvas.isAnimating) {
                const innerWidth = this.innerBounds.width;
                const matrixValues = matrix.extract();
                const isOnLeftSide = (matrixValues.x + (bbox.x2 - bbox.x1) / 2) < innerWidth / 2;
                let progress = Math.clamp((this.animationState.flyInProgress - word.animationDelay) / (1 - word.animationDelay), 0, 1);
                opacity = Math.min(progress * 2, 1);
                progress = 1 - (calcEaseInOut(progress / 2 + 0.5) - 0.5) * 2;

                matrix = matrix.translate(
                    (isOnLeftSide ? -1 : 1) * this.canvas.CANVAS_WIDTH / 2 * progress,
                    0
                );
            }

            const matrixValues = matrix.extract();
            const pathStyles = {
                transform: getCSSTransform({
                    translateX: matrixValues.x,
                    translateY: matrixValues.y
                }),
                fill: this.getWordColor(styles, parseInt(word.layout.weight)),
                opacity,
                transition: transition ? "300ms" : "none"
            };

            word.ref = React.createRef();

            this.renderedWords.push(word);

            children.push(<path key={children.length} ref={word.ref} d={path.toPathData()} style={pathStyles} />);
        }

        return <SVGGroup key={this.id}>{children}</SVGGroup>;
    }

    getWordColor(styles, weight) {
        let bgColor = this.getBackgroundColor();

        if (this.canvas.getSlideColor() != ForeColorType.COLORFUL) {
            let baseColor = this.canvas.getTheme().palette.getForeColor(styles.color, this.getSlideColor(), this.getBackgroundColor());

            if (this.model.invertColors) {
                weight = 6 - weight;
            }
            let color;

            switch (this.canvas.getTheme().palette.getBackgroundColorType(bgColor)) {
                case BackgroundStyleType.DARK:
                    color = baseColor.lighten(1.5 * weight * weight);
                    break;
                case BackgroundStyleType.LIGHT:
                    color = baseColor.darken(1 * weight * weight);
                    break;
                case BackgroundStyleType.COLOR:
                    color = baseColor.lighten(1.5 * weight * weight);
                    break;
                default:
                    color = baseColor;
            }

            return color.toHexString();
        } else {
            let colorNames = this.canvas.getTheme().palette.getColorfulColors();

            if (this.model.invertColors) {
                weight = 6 - weight;
            }
            return this.canvas.getTheme().palette.getColor(colorNames[(weight - 1) % colorNames.length]).toHexString();
        }
    }

    /**
     * Makes sure the generation process is single thread and executes only the last request
     * that comes while there's an executing generation request
     */
    async generateWordCloud(size, undo) {
        if (!this.generateWordCloudPromise) {
            this.generateWordCloudPromise = (async () => {
                // Will generate word cloud with current args
                await this._generateWordCloud(size, undo);

                // Will be calling next generation promises until they
                // stop appearing
                while (this.getNextGenerateWordCloudPromise) {
                    // getNextGenerateWordCloudPromise() will remove itself when executed
                    // so we will go to a next cycle run if there was/were new requests
                    // during the execution
                    await this.getNextGenerateWordCloudPromise();
                }

                // Done, remove the promise
                this.generateWordCloudPromise = null;
            })();
            return;
        }

        // Just overwrite the next generate promise so it'll be executed after the current run
        this.getNextGenerateWordCloudPromise = () => {
            this.getNextGenerateWordCloudPromise = null;
            return this._generateWordCloud(size, undo);
        };
    }

    async _generateWordCloud(size, undo) {
        const wordList = await this._generateWordList(size);

        // Build our new model from the properties set by the word cloud generator on the wordlist
        this.wordList = wordList.map(({ word, weight, layout, drawn }) => ({
            word,
            weight,
            layout,
            drawn
        }));
        this.model.generatedForSize = size.toArray();
        this.model.version = 2;
        this.needsLayoutMigration = false;

        // Refresh so the new model gets rendered
        this.refreshElement();

        await this.canvas.saveCanvasModel({ undo });
    }

    _generateWordList(size) {
        // Create an off-DOM canvas to render wordcloud to
        const $wordCloud = $.div("wordcloud");
        const $canvas = $wordCloud.addEl($(`<canvas width='${size.width * 2}' height='${size.height * 2}'></canvas>`));

        const hasShape = false;

        // Setup word list
        let wordList;
        switch (this.model.sort || "size") {
            case "size":
                wordList = _.reverse(_.sortBy(this.wordList, o => o.weight));
                break;
            case "random":
                wordList = _.shuffle(this.wordList);
                break;
        }

        // Strip any quotes
        for (const word of wordList) {
            if (word.word.startsWith('"') && word.word.endsWith('"')) {
                word.word = word.word.substr(1, word.word.length - 2);
            }
        }

        // Create options for word cloud generator
        const options = {
            list: wordList,
            weightFactor: this.scale,
            gridSize: this.styles.gridSize || 0,
            shuffle: true,
            drawOutOfBound: true,
            shape: "circle",
            clearCanvas: hasShape ? false : true,
            spacing: this.spacing,
            fontCallback: weight => {
                return `${weight === "5" ? 700 : 400} ${weight * this.scale}px "${this.fontId}"`;
            }
        };

        switch (this.style) {
            case "straight":
                options.rotateRatio = 0;
                break;
            case "right_angles":
                options.rotateRatio = 0.33;
                options.rotationSteps = 2;
                break;
            case "rotated":
                options.rotateRatio = 0.60;
                options.rotationSteps = 6;
                break;
        }

        // Run the word cloud gen
        WordCloud($canvas[0], options);

        return new Promise(resolve => $canvas.on("wordcloudstop", () => resolve(wordList)));
    }

    get animationElementName() {
        return "Word Cloud";
    }

    _getAnimations() {
        return [{
            name: "Word Cloud",
            defaultDuration: 2000,
            prepare: () => {
                this.animationWordList = this.wordList;

                this.animationState.flyInProgress = 0;

                for (const word of this.animationWordList) {
                    word.animationDelay = Math.random() * 0.5;
                }
            },
            onBeforeAnimationFrame: progress => {
                this.animationState.flyInProgress = progress;
            }
        }];
    }
}

export { WordCloudElement };

export const elements = {
    WordCloudElement,
};
